import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile, errMsg, filterHazards, nzDateTime } from "@/lib/hsUtil";
import HazardFilters from "./HazardFilters";
import HazardForm from "./HazardForm";

const COLS = [["ref_id", "ID"], ["build_phase", "Build phase"], ["activity", "Activity"], ["hazard", "Hazard"],
  ["initial_risk", "Initial"], ["controls", "Controls"], ["residual_risk", "Residual"],
  ["swms_required", "SWMS"], ["critical_risk", "Critical"]];

const HazardRow = ({ h, on, onTick, onEdit, onArchive, onRestore }) => (
  <tr className={h.archived ? "opacity-50" : ""} data-testid={`hs-hazard-row-${h.id}`}>
    <td><input type="checkbox" checked={on} onChange={() => onTick(h.id)} data-testid={`hs-hazard-tick-${h.id}`}/></td>
    {COLS.map(([k]) => <td key={k} className={k === "controls" ? "max-w-md" : ""}>{h[k]}</td>)}
    <td className="whitespace-nowrap">{h.jobs_using || 0}</td>
    <td className="whitespace-nowrap text-xs" data-testid={`hs-hazard-changed-${h.id}`}>{h.updated_by || ""}<div className="opacity-60">{nzDateTime(h.updated_at)}</div></td>
    <td className="whitespace-nowrap">
      {h.archived
        ? <><span className="text-amber-400 text-xs mr-2">Archived</span>
            <button className="btn-ghost text-xs" onClick={() => onRestore(h)} data-testid={`hs-hazard-restore-${h.id}`}>Restore</button></>
        : <><button className="btn-ghost text-xs mr-1" onClick={() => onEdit(h)} data-testid={`hs-hazard-edit-${h.id}`}>Edit</button>
            <button className="btn-ghost text-xs" onClick={() => onArchive(h)} data-testid={`hs-hazard-remove-${h.id}`}>Remove</button></>}
    </td>
  </tr>
);

export default function HazardsTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ q: "", phase: "", risk: "", flag: "" });
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const fileRef = useRef(null);
  const load = () => api.get("/hs/hazard-library").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => filterHazards((rows || []).filter(h => showArchived || !h.archived), f), [rows, f, showArchived]);
  const shownIds = useMemo(() => new Set(shown.map(h => h.id)), [shown]);
  const sel = [...ticked].filter(id => shownIds.has(id));
  const allOn = shown.length > 0 && sel.length === shown.length;
  const tick = id => setTicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tickAll = () => setTicked(allOn ? new Set() : new Set(shown.map(h => h.id)));
  async function bulk(action) {
    const ids = sel.filter(id => { const h = rows.find(r => r.id === id); return action === "archive" ? !h.archived : h.archived; });
    if (!ids.length) { toast.error(action === "archive" ? "No active hazards ticked" : "No archived hazards ticked"); return; }
    if (action === "archive") {
      if (ids.length > 5) {
        const typed = window.prompt(`Archive ${ids.length} hazards? Projects already using them keep them, marked archived. Type ARCHIVE to confirm.`);
        if (typed !== "ARCHIVE") { if (typed !== null) toast.error("Not archived: you must type ARCHIVE"); return; }
      } else if (!window.confirm(`Archive ${ids.length} hazard(s)? You can restore them later.`)) return;
    }
    try {
      const { data } = await api.post("/hs/hazard-library/bulk", { ids, action });
      toast.success(`${data.changed} hazard(s) ${action === "archive" ? "archived" : "restored"}`);
      setTicked(new Set()); load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function onFile(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/hs/hazard-library/import", fd);
      toast.success(`Import done: ${data.added} added, ${data.updated} updated, ${data.unchanged} unchanged, ${data.skipped} skipped`
        + (data.columns_ignored?.length ? `. Ignored columns: ${data.columns_ignored.join(", ")}` : ""), { duration: 8000 });
      load();
    } catch (err) { toast.error(errMsg(err, "Import failed")); } finally { setBusy(false); }
  }
  async function archive(h) {
    const msg = h.jobs_using ? `"${h.hazard}" is used on ${h.jobs_using} project(s). It will stay on those projects marked archived. Remove it from the master register?`
      : `Remove "${h.hazard}" from the master register? You can restore it later.`;
    if (!window.confirm(msg)) return;
    try { await api.post(`/hs/hazard-library/${h.id}/archive`); toast.success("Hazard archived"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  }
  const restore = async h => {
    try { await api.post(`/hs/hazard-library/${h.id}/restore`); toast.success("Hazard restored"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const exp = ext => downloadFile(`/hs/hazard-library.${ext}`, `master_hazard_register.${ext}`, { include_archived: showArchived })
    .catch(e => toast.error(errMsg(e)));

  return (
    <div className="space-y-3" data-testid="hs-hazards-tab">
      <div className="card flex flex-wrap items-center gap-2">
        <h3 className="text-silver mr-2">Master hazard register</h3>
        <button className="btn-silver" onClick={() => setEditing({})} data-testid="hs-hazard-add-btn">Add hazard</button>
        <button className="btn-ghost" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="hs-hazard-import-btn">{busy ? "Importing…" : "Import CSV"}</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="hs-hazard-import-input"/>
        <button className="btn-ghost" onClick={() => exp("csv")} data-testid="hs-hazard-export-csv">Export CSV</button>
        <button className="btn-ghost" onClick={() => exp("pdf")} data-testid="hs-hazard-export-pdf">Export PDF</button>
        <div className="text-xs opacity-60 w-full">Import merges by hazard ID: existing IDs are updated, new IDs are added. Importing the same file twice makes no duplicates.</div>
      </div>
      <div className="card space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <HazardFilters rows={rows || []} f={f} setF={setF} prefix="hs-hazard"/>
          <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-testid="hs-hazard-show-archived"/> Show archived</label>
          <span className="text-xs opacity-60" data-testid="hs-hazard-count">{shown.length} of {(rows || []).length}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="hs-hazard-bulk-bar">
          <span data-testid="hs-hazard-selected-msg">{sel.length ? `${sel.length} of ${shown.length} shown hazards selected` : "No hazards selected"}</span>
          <button className="btn-ghost text-xs" disabled={!sel.length} onClick={() => bulk("archive")} data-testid="hs-hazard-bulk-archive">Archive selected</button>
          <button className="btn-ghost text-xs" disabled={!sel.length} onClick={() => bulk("restore")} data-testid="hs-hazard-bulk-restore">Restore selected</button>
          {sel.length > 0 && <button className="text-xs underline opacity-70" onClick={() => setTicked(new Set())} data-testid="hs-hazard-untick-all">Untick all</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="table w-full text-xs" data-testid="hs-hazard-table">
            <thead><tr><th><input type="checkbox" checked={allOn} onChange={tickAll} title="Select all shown" data-testid="hs-hazard-tick-all"/></th>{COLS.map(c => <th key={c[0]}>{c[1]}</th>)}<th>Projects</th><th>Last changed</th><th></th></tr></thead>
            <tbody>
              {shown.map(h => <HazardRow key={h.id} h={h} on={ticked.has(h.id)} onTick={tick} onEdit={setEditing} onArchive={archive} onRestore={restore}/>)}
              {rows && shown.length === 0 && <tr><td colSpan={13} className="opacity-60">No hazards match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <HazardForm hazard={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}
